import { CardStackIcon } from "@radix-ui/react-icons";
import { Clock3, DollarSign, GitBranch, MessageSquare } from "lucide-react";
import type { ReactNode } from "react";

import { BlockIcon, rawBlockTypeToBlockType } from "@/components/PowerBlock/Blocks";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { cn } from "@/lib/utils";
import { EntityType } from "@/queries/folder-entities/types";
import { ColumnType, type SmartColumn } from "@/queries/smart-tables/types";

type SmartColumnIconTone = "default" | "muted" | "selected";

interface SmartColumnIconConfig {
  icon: ReactNode;
  label: string;
  isDataset: boolean;
  isText?: boolean;
}

interface SmartColumnHeaderIconProps {
  columnType?: ColumnType;
  config?: Record<string, unknown> | null;
  metricKey?: string | null;
  className?: string;
  tone?: SmartColumnIconTone;
  title?: string;
}

interface SmartColumnTypeBadgeProps extends SmartColumnHeaderIconProps {
  showLabel?: boolean;
  labelClassName?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getColumnTypeFromConfig = (config?: Record<string, unknown> | null): ColumnType | null => {
  if (!isRecord(config)) {
    return null;
  }

  const configuredType = config.block_type;
  if (
    typeof configuredType === "string" &&
    Object.values(ColumnType).includes(configuredType as ColumnType)
  ) {
    return configuredType as ColumnType;
  }

  return null;
};

const getEffectiveColumnType = (
  columnType?: ColumnType,
  config?: Record<string, unknown> | null,
): ColumnType | undefined => {
  const configType = getColumnTypeFromConfig(config);
  if (configType) {
    return configType;
  }

  if (columnType === ColumnType.SMART_TABLE_REFERENCE) {
    return ColumnType.PROMPT_TEMPLATE;
  }

  return columnType;
};

const toneClassNameMap: Record<SmartColumnIconTone, string> = {
  default: "text-muted-foreground",
  muted: "text-muted-foreground/75",
  selected: "text-foreground",
};

const datasetToneClassNameMap: Record<SmartColumnIconTone, string> = {
  default: "text-muted-foreground/80",
  muted: "text-muted-foreground/65",
  selected: "text-foreground",
};

const sharedIconClassName = "h-3.5 w-3.5";

const textToneClassNameMap: Record<SmartColumnIconTone, string> = {
  default: "text-muted-foreground/90",
  muted: "text-muted-foreground/70",
  selected: "text-foreground",
};

const renderColumnTypeIcon = (columnType: ColumnType, tone: SmartColumnIconTone): ReactNode => {
  if (columnType === ColumnType.TRACE) {
    return <GitBranch className={cn(sharedIconClassName, toneClassNameMap[tone])} />;
  }

  if (columnType === ColumnType.REQUEST) {
    return <CardStackIcon className={cn(sharedIconClassName, toneClassNameMap[tone])} />;
  }

  const blockType = rawBlockTypeToBlockType(columnType as never);
  const Icon = blockType ? BlockIcon[blockType] : null;

  if (!Icon) {
    return (
      <EntityIcon type={EntityType.PROMPT} size="xs" className={datasetToneClassNameMap[tone]} />
    );
  }

  return <Icon className={cn(sharedIconClassName, toneClassNameMap[tone])} />;
};

const renderDatasetIcon = (tone: SmartColumnIconTone): ReactNode => (
  <EntityIcon type={EntityType.DATASET} size="xs" className={datasetToneClassNameMap[tone]} />
);

const renderMetricIcon = (metricKey: string, tone: SmartColumnIconTone): ReactNode | null => {
  if (metricKey === "price") {
    return <DollarSign className={cn(sharedIconClassName, toneClassNameMap[tone])} />;
  }

  if (metricKey === "latency_ms") {
    return <Clock3 className={cn(sharedIconClassName, toneClassNameMap[tone])} />;
  }

  if (metricKey === "response") {
    return <MessageSquare className={cn(sharedIconClassName, toneClassNameMap[tone])} />;
  }

  return null;
};

const getMetricIconLabel = (metricKey: string): string => {
  if (metricKey === "price") {
    return "Price metadata";
  }
  if (metricKey === "latency_ms") {
    return "Latency metadata";
  }
  if (metricKey === "response") {
    return "Response metadata";
  }
  return "Metadata";
};

export const getSmartColumnIconConfig = (
  columnType?: ColumnType,
  config?: Record<string, unknown> | null,
  tone: SmartColumnIconTone = "default",
  metricKey?: string | null,
): SmartColumnIconConfig => {
  if (metricKey) {
    const icon = renderMetricIcon(metricKey, tone);
    if (icon) {
      return {
        icon,
        label: getMetricIconLabel(metricKey),
        isDataset: false,
      };
    }
  }

  const effectiveColumnType = getEffectiveColumnType(columnType, config);

  if (columnType === ColumnType.TEXT) {
    return {
      icon: (
        <span className={cn("text-[10px] font-semibold leading-none", textToneClassNameMap[tone])}>
          Aa
        </span>
      ),
      label: "Text column",
      isDataset: false,
      isText: true,
    };
  }

  if (!effectiveColumnType || effectiveColumnType === ColumnType.DATASET) {
    return {
      icon: renderDatasetIcon(tone),
      label: "Dataset column",
      isDataset: true,
      isText: false,
    };
  }

  let label = "Smart column";

  if (effectiveColumnType === ColumnType.PROMPT_TEMPLATE) {
    label = "Prompt column";
  } else if (effectiveColumnType === ColumnType.WORKFLOW) {
    label = "Agent column";
  } else if (effectiveColumnType === ColumnType.TRACE) {
    label = "Trace column";
  } else if (effectiveColumnType === ColumnType.REQUEST) {
    label = "Request column";
  } else if (effectiveColumnType === ColumnType.CODE_EXECUTION) {
    label = "Code column";
  } else if (effectiveColumnType === ColumnType.LLM_ASSERTION) {
    label = "AI Judge column";
  }

  return {
    icon: renderColumnTypeIcon(effectiveColumnType, tone),
    label,
    isDataset: false,
  };
};

export const getSmartColumnAriaLabel = (
  columnType?: ColumnType,
  config?: Record<string, unknown> | null,
): string => getSmartColumnIconConfig(columnType, config).label;

export const SmartColumnHeaderIcon = ({
  columnType,
  config,
  metricKey,
  className,
  tone = "default",
  title,
}: SmartColumnHeaderIconProps) => {
  const { icon, label, isDataset, isText } = getSmartColumnIconConfig(
    columnType,
    config,
    tone,
    metricKey,
  );

  return (
    <span
      className={cn(
        "inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-colors",
        isDataset && "border-border/60 bg-muted/35",
        isText && "border-border/70 bg-background/80",
        !isDataset &&
          !isText &&
          "border-border/70 bg-background shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]",
        className,
      )}
      title={title ?? label}
      aria-hidden="true"
    >
      {icon}
    </span>
  );
};

export const SmartColumnTypeBadge = ({
  columnType,
  config,
  className,
  tone = "default",
  showLabel = false,
  labelClassName,
  title,
}: SmartColumnTypeBadgeProps) => {
  const { label } = getSmartColumnIconConfig(columnType, config, tone);

  return (
    <span
      className={cn("inline-flex min-w-0 items-center gap-1.5", className)}
      title={title ?? label}
    >
      <SmartColumnHeaderIcon columnType={columnType} config={config} tone={tone} />
      {showLabel ? (
        <span
          className={cn(
            "truncate text-xs font-medium",
            tone === "selected" ? "text-foreground" : "text-muted-foreground",
            labelClassName,
          )}
        >
          {label}
        </span>
      ) : null}
    </span>
  );
};

export const getSmartColumnHeaderIconProps = (column?: Pick<SmartColumn, "type" | "config">) => {
  if (!column) {
    return {
      columnType: undefined,
      config: null,
      title: "Dataset column",
    };
  }

  return {
    columnType: column.type,
    config: isRecord(column.config) ? column.config : null,
    title: getSmartColumnAriaLabel(column.type, column.config),
  };
};

export type { SmartColumnHeaderIconProps, SmartColumnIconConfig, SmartColumnTypeBadgeProps };
